import { cn } from '@quadralize/utils'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import * as React from 'react'

const ScrollArea = React.forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    scrollbarSize?: 'default' | 'sm'
    orientation?: 'vertical' | 'horizontal' | 'both'
  }
>(
  (
    {
      className,
      children,
      type = 'auto',
      scrollbarSize = 'default',
      orientation = 'both',
      ...props
    },
    ref,
  ) => (
    <ScrollAreaPrimitive.Root
      ref={ref}
      type={type}
      className={cn('relative overflow-hidden flex min-h-0 flex-col', className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        data-radix-scroll-area-viewport=""
        className={cn(
          'flex-1 min-h-0 w-full rounded-[inherit] outline-none',
          orientation === 'vertical' && 'overflow-x-hidden',
          orientation === 'horizontal' && 'overflow-y-hidden',
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      {(orientation === 'vertical' || orientation === 'both') && <ScrollBar size={scrollbarSize} />}
      {(orientation === 'horizontal' || orientation === 'both') && (
        <ScrollBar orientation="horizontal" size={scrollbarSize} />
      )}
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  ),
)
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.Scrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Scrollbar> & {
    size?: 'default' | 'sm'
  }
>(({ className, orientation = 'vertical', size = 'default', ...props }, ref) => (
  <ScrollAreaPrimitive.Scrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'flex touch-none select-none transition-colors p-px',
      orientation === 'vertical' && [
        'h-full border-l border-l-transparent',
        size === 'default' ? 'w-2' : 'w-1.5',
      ],
      orientation === 'horizontal' && [
        'flex-col border-t border-t-transparent',
        size === 'default' ? 'h-2' : 'h-1.5',
      ],
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.Thumb className="relative flex-1 rounded-full bg-text-tertiary/40 hover:bg-text-tertiary/60 transition-colors" />
  </ScrollAreaPrimitive.Scrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.Scrollbar.displayName

export { ScrollArea, ScrollBar }
